import { useState, useEffect } from "react";
import { validateCafeDetailsForm } from "../../../validations/generalForm";
import { useToastPromise } from "../../../components/toastPromise";
import { GeneralController } from "../../../controllers/generalController";
import Button from "@mui/material/Button";
import "../../../assets/css/setttings.css";
import { Textbox } from "../../../components/textbox";
import { RichText } from "../../../components/richText";
import { TableName } from "../../../constants/tableName";
import { RichTextPreview } from "../../../components/richTextPreview";
import { MediaUploaderPreview } from "../../collaboration/mediaUploaderPreview";
import { StorageController } from "../../../controllers/storageController";
import { FileUtil } from "../../../util/fileUtil";

export const CafeDetails = ({ user }) => {
  const toastPromise = useToastPromise();
  const emptyHTMLString = "<p>&nbsp;</p>";
  const initialInputs = {
    cafeAddress: "",
    cafeEmail: "",
    cafeContactNum: "",
    operationStartTime: "",
    operationEndTime: "",
    copyrightLabel: "",
    poweredByLabel: "",
    privacyPolicy: emptyHTMLString,
    termsAndConditions: emptyHTMLString,
    cafeLogo: null,
  };
  const [inputs, setInputs] = useState(initialInputs);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isPrivacyPolicyPreviewDisabled, setIsPrivacyPolicyPreviewDisabled] =
    useState(true);
  const [
    isTermsAndConditionsPreviewDisabled,
    setIsTermsAndConditionsPreviewDisabled,
  ] = useState(true);
  const [isPrivacyPolicyPreviewOpen, setIsPrivacyPolicyPreviewOpen] =
    useState(false);
  const [isTermsAndConditionsPreviewOpen, setIsTermsAndConditionsPreviewOpen] =
    useState(false);
  const [cafeLogo, setCafeLogo] = useState();
  const [newCafeLogoULR, setNewCafeLogoURL] = useState();
  const [currentCafeLogoULR, setCurrentCafeLogoURL] = useState();
  const saveInputs = (name, value) => {
    setInputs((values) => ({ ...values, [name]: value }));
  };
  const resetForm = () => {
    setInputs(initialInputs);
    setErrors({});
    setIsPrivacyPolicyPreviewDisabled(true);
    setIsTermsAndConditionsPreviewDisabled(true);
    setCafeLogo();
    setNewCafeLogoURL();
    setCurrentCafeLogoURL();
  };
  const handleSubmit = (event) => {
    event.preventDefault();
    const newErrors = validateCafeDetailsForm(inputs);
    setErrors(newErrors);
    if (Object.keys(newErrors).length !== 0) {
      console.debug("Form submission failed due to validation errors.");
      return;
    }
    console.debug("Form validated successfully.");
    uploadImageToFirebase("cafe_logo", cafeLogo ? [cafeLogo] : []).then(
      (cafeLogoURL) => {
        setNewCafeLogoURL(cafeLogoURL);
        saveGeneral(cafeLogoURL);
      }
    );
  };
  const saveGeneral = (cafeLogoURL) => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        inputs.id
          ? GeneralController.update({
              parameters: {
                ...inputs,
                id: inputs.id,
                cafeLogo:
                  cafeLogoURL.length === 1 ? cafeLogoURL : currentCafeLogoULR,
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            })
          : GeneralController.add({
              parameters: {
                ...inputs,
                cafeLogo: cafeLogoURL,
                createdBy: user.uid,
                createdDate: new Date().toLocaleString(),
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            });
      }),
      successMessage: (data) => {
        console.debug(data);
        return data;
      },
      errorMessage: (data) => data,
    }).finally(() => {
      setLoading(false);
      resetForm();
      getAll();
    });
  };
  const uploadImageToFirebase = (folderName, files) => {
    setLoading(true);
    return new Promise((resolve, reject) => {
      StorageController.addAll({
        folderName: folderName,
        files: files,
        resolve: resolve,
        reject: reject,
      });
    });
  };
  const handleTextChange = (event) => {
    saveInputs(event.target.name, event.target.value);
  };
  const handleRichTextChange = (event, editor) => {
    const data = editor.getData();
    saveInputs(editor.config.get("name"), data);
    if (editor.config.get("name") === "privacyPolicy")
      setIsPrivacyPolicyPreviewDisabled(data?.trim() === emptyHTMLString);
    if (editor.config.get("name") === "termsAndConditions")
      setIsTermsAndConditionsPreviewDisabled(data?.trim() === emptyHTMLString);
  };
  const handleCafeLogoChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const selectedCafeLogo = await FileUtil.toWebp(file);
    setCafeLogo(selectedCafeLogo);
    const previewURLs = URL.createObjectURL(selectedCafeLogo);
    setNewCafeLogoURL(previewURLs);
    saveInputs(event.target.name, selectedCafeLogo);
  };
  const getAll = () => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        GeneralController.getAll({
          resolve: resolve,
          reject: reject,
        });
      }),
      successMessage: (data) => {
        const fetchedData = data.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        if (fetchedData.length > 0) {
          setInputs(fetchedData[0]);
          setCurrentCafeLogoURL(fetchedData[0].cafeLogo);
          setIsPrivacyPolicyPreviewDisabled(
            fetchedData[0].privacyPolicy === "" ||
              fetchedData[0].privacyPolicy?.trim() === emptyHTMLString
          );
          setIsTermsAndConditionsPreviewDisabled(
            fetchedData[0].termsAndConditions === "" ||
              fetchedData[0].termsAndConditions?.trim() === emptyHTMLString
          );
        }
        return "Collaboration details retrieved successfully.";
      },
      errorMessage: (data) => data,
    }).finally(() => setLoading(false));
  };
  useEffect(() => {
    getAll();
  }, []);

  return (
    <section id="cafeDetails">
      <h4>CAFE</h4>
      <hr />
      <Button onClick={handleSubmit} disabled={loading}>
        {loading ? "Saving" : "Save"}
      </Button>
      <form onSubmit={handleSubmit}>
        <div>
          <Textbox
            type="text"
            name="cafeAddress"
            placeholder="Cafe Address"
            value={inputs.cafeAddress}
            onChange={handleTextChange}
            errorMsg={errors.cafeAddress}
          />
          <Textbox
            type="text"
            name="cafeEmail"
            placeholder="Cafe Email"
            value={inputs.cafeEmail}
            onChange={handleTextChange}
            errorMsg={errors.cafeEmail}
          />
          <Textbox
            type="text"
            name="cafeContactNum"
            placeholder="Cafe Contact Number"
            value={inputs.cafeContactNum}
            onChange={handleTextChange}
            errorMsg={errors.cafeContactNum}
          />
          <Textbox
            type="time"
            name="operationStartTime"
            placeholder="Operation Start Time"
            value={inputs.operationStartTime}
            onChange={handleTextChange}
            errorMsg={errors.operationStartTime}
          />
          <Textbox
            type="time"
            name="operationEndTime"
            placeholder="Operation End Time"
            value={inputs.operationEndTime}
            onChange={handleTextChange}
            errorMsg={errors.operationEndTime}
          />
          <Textbox
            type="text"
            name="copyrightLabel"
            placeholder="Copyright Label"
            value={inputs.copyrightLabel}
            onChange={handleTextChange}
            errorMsg={errors.copyrightLabel}
          />
          <Textbox
            type="text"
            name="poweredByLabel"
            placeholder="Powered By Label"
            value={inputs.poweredByLabel}
            onChange={handleTextChange}
            errorMsg={errors.poweredByLabel}
          />
          <RichText
            name="privacyPolicy"
            onChange={handleRichTextChange}
            placeholder="Privacy Policy"
            data={inputs.privacyPolicy || emptyHTMLString}
            errorMsg={errors.privacyPolicy}
            tableName={TableName.GENERAL}
          />
          <Button
            onClick={() => setIsPrivacyPolicyPreviewOpen(true)}
            disabled={isPrivacyPolicyPreviewDisabled}
          >
            Preview
          </Button>
          <RichTextPreview
            isPreviewOpen={isPrivacyPolicyPreviewOpen}
            handlePreviewClose={() => setIsPrivacyPolicyPreviewOpen(false)}
            content={inputs.privacyPolicy}
          />
          <RichText
            name="termsAndConditions"
            onChange={handleRichTextChange}
            placeholder="Terms And Conditions"
            data={inputs.termsAndConditions || emptyHTMLString}
            errorMsg={errors.termsAndConditions}
            tableName={TableName.GENERAL}
          />
          <Button
            onClick={() => setIsTermsAndConditionsPreviewOpen(true)}
            disabled={isTermsAndConditionsPreviewDisabled}
          >
            Preview
          </Button>
          <RichTextPreview
            isPreviewOpen={isTermsAndConditionsPreviewOpen}
            handlePreviewClose={() => setIsTermsAndConditionsPreviewOpen(false)}
            content={inputs.termsAndConditions}
          />
          <MediaUploaderPreview
            onChange={handleCafeLogoChange}
            multiple={false}
            name="cafeLogo"
            placeholder="Cafe Logo"
            errorMsg={errors.cafeLogo}
            newList={newCafeLogoULR ? [newCafeLogoULR] : []}
            currentList={currentCafeLogoULR ? [currentCafeLogoULR] : []}
          />
        </div>
      </form>
    </section>
  );
};
